/**
 * The mascot in the corner of every signed-in page, and the window it opens: the live
 * match between ALGO AI and AI Trader (docs/ai-match.md). Display only. When the API says
 * it's off for this person, nothing at all is drawn.
 */
import { ArrowDown, ArrowLeftRight, Minus } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { Button, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";

import {
  markSeen,
  readOpen,
  readSide,
  signedINR,
  timeIST,
  unseenMoves,
  useAiMatch,
  useAiMatchFeed,
  writeOpen,
  writeSide,
  type Feed,
  type MarketFilter,
  type Me,
  type ModelFilter,
  type Side,
  type Summary,
  type TabKey,
  type Who,
} from "./api";
import { Mascot } from "./Mascot";
import { GistTab, LiveTab, PicksTab, ScorecardTab } from "./tabs";

const TAB_LABEL: Record<TabKey, string> = {
  live: "Live match",
  scorecard: "Scorecard",
  picks: "Picks",
  gist: "Gist",
};
const NOTICE = "Paper trades, not calls or recommendations. No real money.";

function Net({ label, who, s }: { label: string; who: Who; s: Summary }) {
  const value = s.net[who] ?? null;
  const open = s.open[who] ?? null;
  const openNet = s.open_net[who] ?? null;
  return (
    <div className="min-w-0">
      <div className="text-[11.5px] opacity-85">{label}</div>
      <div className="num truncate text-[17px] font-bold">{value == null ? "—" : signedINR(value)}</div>
      <div className="truncate text-[11px] opacity-85">closed, after costs</div>
      {open && openNet != null ? (
        <div className="num truncate text-[11px] opacity-85">
          {open} open {signedINR(openNet)}
        </div>
      ) : null}
    </div>
  );
}

/** The gist text, with the two names in bold wherever the admin's wording has them. */
function GistText({ text }: { text: string }) {
  return (
    <>
      {text.split(/(ALGO AI|AI Trader)/).map((part, i) =>
        part === "ALGO AI" || part === "AI Trader" ? (
          <b key={i} className="text-fg">
            {part}
          </b>
        ) : (
          part
        ),
      )}
    </>
  );
}

function Panel({
  me,
  summary,
  side,
  leftClass,
  onSwap,
  onClose,
  onLost,
}: {
  me: Me;
  summary: Summary;
  side: Side;
  leftClass: string;
  onSwap: () => void;
  onClose: () => void;
  onLost: () => void;
}) {
  const tabs = me.tabs?.length ? me.tabs : (["live"] as TabKey[]);
  const [tab, setTab] = useState<TabKey>(tabs[0] ?? "live");
  const [market, setMarket] = useState<MarketFilter>("all");
  const [model, setModel] = useState<ModelFilter>("both");
  const q = useAiMatchFeed(true, market, model);
  const panel = useRef<HTMLDivElement>(null);
  const s = q.data?.summary ?? summary;
  const name = me.name ?? "";
  const failed = q.isError;

  useEffect(() => panel.current?.focus(), []);
  // an admin may switch it off while it's open: ask again and the window goes
  useEffect(() => {
    if (failed) onLost();
  }, [failed, onLost]);

  // a tab the admin just removed
  const current = tabs.includes(tab) ? tab : (tabs[0] ?? "live");
  const data: Feed | undefined = q.data;
  const scroller = useRef<HTMLDivElement>(null);
  const pinned = useRef(true); // at the latest line: new lines keep it there
  const [away, setAway] = useState(false);
  const items = data?.items;

  const pin = () => {
    pinned.current = true;
    setAway(false);
  };
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && pinned.current) el.scrollTop = el.scrollHeight;
  }, [items, current]);
  useEffect(() => markSeen(s.today, s.moves), [s.today, s.moves]);

  return (
    <div
      ref={panel}
      role="dialog"
      aria-label={`${name}: ALGO AI vs AI Trader`}
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      className={cn(
        "fixed inset-0 z-40 flex flex-col overflow-hidden bg-surface outline-none",
        "pt-[var(--safe-top)] pb-[var(--safe-bottom)]",
        "sm:inset-auto sm:bottom-4 sm:h-[min(660px,calc(100dvh-2rem))] sm:w-[400px]",
        side === "right" ? "sm:right-4" : cn("sm:left-4", leftClass),
        "sm:rounded-[18px] sm:border sm:border-border sm:pt-0 sm:pb-0 sm:shadow-lift",
      )}
    >
      <div className="flex shrink-0 items-center gap-2.5 bg-[#0b1b45] py-2.5 pr-1.5 pl-3 text-white">
        <Mascot mood={s.mood} className="size-10 shrink-0" />
        <div className="min-w-0 flex-1">
          <b className="block truncate leading-tight">{name}</b>
          <span className="block truncate text-xs opacity-80">ALGO AI vs AI Trader</span>
          <span className="block truncate text-[11px] opacity-80">
            {formatDate(s.today, "EEE d MMM")} · updated {timeIST(s.as_of)} IST
          </span>
        </div>
        {s.market_open ? (
          <span className="shrink-0 animate-pulse-dot rounded-full bg-[#e0322a] px-2 py-0.5 text-[11px] font-bold text-white">
            LIVE
          </span>
        ) : null}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Move to the ${side === "left" ? "right" : "left"}`}
          className="text-white hover:bg-white/15 hover:text-white"
          onClick={onSwap}
        >
          <ArrowLeftRight />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Minimize"
          className="text-white hover:bg-white/15 hover:text-white"
          onClick={onClose}
        >
          <Minus />
        </Button>
      </div>

      <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 bg-[#10275f] px-3 py-2 text-center text-white">
        <Net label="ALGO AI" who="algo" s={s} />
        <span className="rounded-full bg-[#f0a71f] px-[7px] py-[3px] text-[11px] font-extrabold text-[#3b2600]">
          VS
        </span>
        <Net label="AI Trader" who="ai" s={s} />
      </div>

      <Tabs value={current} onValueChange={(v) => {
          pin();
          setTab(v as TabKey);
        }}
        className="relative flex min-h-0 flex-1 flex-col">
        <TabsList
          aria-label="AI Match"
          className="h-auto w-full max-w-none shrink-0 justify-start gap-0.5 rounded-none border-b border-border bg-transparent p-0 px-2 pt-1.5"
        >
          {tabs.map((t) => (
            <TabsTrigger
              key={t}
              value={t}
              className={cn(
                "h-8 rounded-none border-b-2 border-transparent px-2.5 text-[13px] font-semibold",
                "data-[state=active]:border-brand data-[state=active]:bg-transparent data-[state=active]:text-brand data-[state=active]:shadow-none",
              )}
            >
              {TAB_LABEL[t]}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map((t) => (
          <TabsContent
            key={t}
            value={t}
            ref={t === "live" ? scroller : undefined}
            onScroll={
              t === "live"
                ? (e) => {
                    const el = e.currentTarget;
                    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
                    pinned.current = gap < 80;
                    setAway(gap > 200);
                  }
                : undefined
            }
            className="mt-0 min-h-0 flex-1 overflow-y-auto bg-bg px-3 py-2.5"
          >
            {!data ? (
              <div className="space-y-2" aria-label="Loading">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </div>
            ) : t === "live" ? (
              <LiveTab
                items={data.items}
                summary={s}
                market={market}
                model={model}
                onMarket={(m) => {
                  pin();
                  setMarket(m);
                }}
                onModel={(m) => {
                  pin();
                  setModel(m);
                }}
              />
            ) : t === "scorecard" ? (
              <ScorecardTab data={data.scorecard} note={s.today_note} />
            ) : t === "picks" ? (
              <PicksTab picks={data.picks ?? []} note={s.today_note} />
            ) : (
              <GistTab gist={data.gist} />
            )}
          </TabsContent>
        ))}
        {away && current === "live" ? (
          <Button
            size="sm"
            className="absolute bottom-2 left-1/2 z-10 -translate-x-1/2 shadow-lift"
            onClick={() => {
              pin();
              const el = scroller.current;
              if (el) el.scrollTop = el.scrollHeight;
            }}
          >
            <ArrowDown /> Latest
          </Button>
        ) : null}
      </Tabs>

      {me.gist ? (
        <div className="shrink-0 border-t border-border bg-surface px-3 py-1.5 text-xs leading-snug text-muted">
          <GistText text={me.gist} />
        </div>
      ) : null}
      <div className="shrink-0 border-t border-border bg-surface px-3 py-1.5 text-[11px] text-muted">{NOTICE}</div>
    </div>
  );
}

/** `menuLeft`: the side menu's width class, when one is there to stay clear of. */
export function AiMatch({ enabled = true, menuLeft = "" }: { enabled?: boolean; menuLeft?: string }) {
  const q = useAiMatch(enabled);
  const [open, setOpenState] = useState(readOpen);
  const [own, setOwn] = useState(readSide);
  const button = useRef<HTMLButtonElement>(null);
  const refocus = useRef(false);
  const refetch = q.refetch;
  const onLost = useCallback(() => void refetch(), [refetch]);

  const setOpen = (on: boolean) => {
    writeOpen(on);
    refocus.current = !on;
    setOpenState(on);
  };

  useEffect(() => {
    if (!open && refocus.current) {
      refocus.current = false;
      button.current?.focus();
    }
  }, [open]);

  const me = q.data;
  if (!enabled || !me?.on || !me.summary) return null;
  const s = me.summary;
  const side: Side = own ?? me.position ?? "left";
  const swap = () => {
    const next = side === "left" ? "right" : "left";
    writeSide(next);
    setOwn(next);
  };
  if (open)
    return (
      <Panel
        me={me}
        summary={s}
        side={side}
        leftClass={menuLeft}
        onSwap={swap}
        onClose={() => setOpen(false)}
        onLost={onLost}
      />
    );
  const fresh = unseenMoves(s.today, s.moves);
  return (
    <button
      ref={button}
      type="button"
      aria-label={`Open ${me.name ?? "AI Match"}: ${s.bubble}${fresh ? `, ${fresh} new` : ""}`}
      onClick={() => setOpen(true)}
      className={cn(
        "fixed z-[29] flex max-w-[calc(100vw-1.5rem)] cursor-pointer items-center gap-2 rounded-full",
        "bottom-[calc(var(--safe-bottom)+4.25rem)] lg:bottom-4",
        side === "right" ? "right-3 sm:right-4" : cn("left-3 flex-row-reverse sm:left-4", menuLeft),
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
      )}
    >
      <span className="min-w-0 truncate rounded-xl border border-border bg-surface px-2.5 py-1 text-xs font-semibold text-fg shadow-lift">
        {s.bubble}
      </span>
      <span className={cn("relative shrink-0", fresh > 0 && "motion-safe:animate-bounce")}>
        <Mascot mood={s.mood} className="size-[52px] drop-shadow-[0_4px_8px_rgb(16_24_40/0.25)] sm:size-[58px]" />
        {fresh > 0 ? (
          <span className="absolute -top-1 -right-1 min-w-5 rounded-full bg-[#e0322a] px-1 text-center text-[11px] leading-5 font-bold text-white">
            {fresh > 99 ? "99+" : fresh}
          </span>
        ) : null}
      </span>
    </button>
  );
}
